import { model } from "@/utils/models/decorator-model";
import { decimals, field, hidden, nullable, print, rightAlign } from "@/utils/models/decorator-field";
import { printLocalDate } from "@/utils/pretty-print";
import Contact from "./contact";
import { Purchase } from ".";

export type FixedAssetMethod = "linear" | "progressive" | "degressive-double" | "degressive-mixed" | "sum-of-years";
export type FixedAssetProRataMode = "none" | "daily" | "percent";

export const FIXED_ASSET_METHODS:FixedAssetMethod[] = ["linear", "progressive", "degressive-double", "degressive-mixed", "sum-of-years"];
export const FIXED_ASSET_PRO_RATA_MODES:FixedAssetProRataMode[] = ["none", "daily", "percent"];

export interface IDepreciationPeriod {
	BookYear:number;
	Index:number;
	Date:Date;
	Amount:number;
	Cumulative:number;
	BookValue:number;
	Historic:boolean;
	Booked:boolean;
	DiverseGroupID:number;
}

export function parseDepreciationPeriod(data:any):IDepreciationPeriod {
	return {
		BookYear: data.BookYear,
		Index: data.Index,
		Date: new Date(data.Date),
		Amount: data.Amount,
		Cumulative: data.Cumulative,
		BookValue: data.BookValue,
		Historic: data.Historic == true,
		Booked: data.Booked == true,
		DiverseGroupID: data.DiverseGroupID || 0
	};
}

@model("FixedAsset")
export class FixedAsset {
	@field("number")
	public ID:number = 0;

	@field("string")
	public Name:string = "";

	@field("string")
	public Description:string = "";

	@field("number")
	@hidden()
	public PurchaseID:number = 0;

	@field("number")
	@hidden()
	public PurchaseBookYear:number = 0;

	@field("has-one", "Purchase")
	@nullable()
	public Purchase:Purchase | null = null;

	@field("number")
	@hidden()
	public ContactID:number = 0;

	@field("has-one", "Contact")
	@nullable()
	public Contact:Contact | null = null;

	@field("date")
	@print(printLocalDate)
	public StartDate:Date = new Date();

	@field("string")
	public Method:FixedAssetMethod = "linear";

	@field("number")
	@rightAlign()
	public Years:number = 5;

	@field("number")
	@decimals(2)
	@rightAlign()
	public AcquisitionValue:number = 0;

	@field("number")
	@decimals(2)
	@rightAlign()
	public ResidualValue:number = 0;

	@field("string")
	public ProRataMode:FixedAssetProRataMode = "daily";

	@field("number")
	@decimals(2)
	public ProRataPercent:number = 0;

	@field("number")
	public FirstBookYear:number = 0;

	@field("number")
	@decimals(2)
	@rightAlign()
	public AlreadyDepreciated:number = 0;

	@field("string")
	public AssetLedgerCode:string = "";

	@field("string")
	public DepreciationLedgerCode:string = "";

	@field("string")
	public CostLedgerCode:string = "";

	@field("number")
	public JournalID:number = 0;

	@field("date")
	@nullable()
	@print(printLocalDate)
	public DisposedAt:Date | null = null;

	@field("string")
	public Comment:string = "";

	@field("date")
	@print(printLocalDate)
	public TimeCreated:Date = new Date();

	// eslint-disable-next-line max-lines-per-function
	public constructor(data?:any) {
		if (!data) return;
		this.ID = data.ID;
		this.Name = data.Name || "";
		this.Description = data.Description || "";
		this.PurchaseID = data.PurchaseID || 0;
		this.PurchaseBookYear = data.PurchaseBookYear || 0;
		if (data.Purchase && data.Purchase.ID) {
			this.Purchase = new Purchase(data.Purchase);
		}
		this.ContactID = data.ContactID || 0;
		if (data.Contact && data.Contact.ID) {
			this.Contact = new Contact(data.Contact);
		}
		this.StartDate = new Date(data.StartDate);
		this.Method = data.Method || "linear";
		this.Years = data.Years || 0;
		this.AcquisitionValue = data.AcquisitionValue || 0;
		this.ResidualValue = data.ResidualValue || 0;
		this.ProRataMode = data.ProRataMode || "none";
		this.ProRataPercent = data.ProRataPercent || 0;
		this.FirstBookYear = data.FirstBookYear || 0;
		this.AlreadyDepreciated = data.AlreadyDepreciated || 0;
		this.AssetLedgerCode = data.AssetLedgerCode || "";
		this.DepreciationLedgerCode = data.DepreciationLedgerCode || "";
		this.CostLedgerCode = data.CostLedgerCode || "";
		this.JournalID = data.JournalID || 0;
		this.DisposedAt = data.DisposedAt ? new Date(data.DisposedAt) : null;
		this.Comment = data.Comment || "";
		this.TimeCreated = data.TimeCreated ? new Date(data.TimeCreated) : new Date();
	}

	public getJSON():any {
		return {
			ID: this.ID,
			Name: this.Name,
			Description: this.Description,
			PurchaseID: this.PurchaseID,
			PurchaseBookYear: this.PurchaseBookYear,
			ContactID: this.ContactID,
			StartDate: this.StartDate.toJSON(),
			Method: this.Method,
			Years: this.Years,
			AcquisitionValue: this.AcquisitionValue,
			ResidualValue: this.ResidualValue,
			ProRataMode: this.ProRataMode,
			ProRataPercent: this.ProRataPercent,
			FirstBookYear: this.FirstBookYear,
			AlreadyDepreciated: this.AlreadyDepreciated,
			AssetLedgerCode: this.AssetLedgerCode,
			DepreciationLedgerCode: this.DepreciationLedgerCode,
			CostLedgerCode: this.CostLedgerCode,
			JournalID: this.JournalID,
			DisposedAt: this.DisposedAt ? this.DisposedAt.toJSON() : null,
			Comment: this.Comment
		};
	}
}
